import SearchIcon from "@mui/icons-material/Search";
import { Box, Button, InputAdornment, TextField } from "@mui/material";
import { Loading } from "shared-ui/global/Loading";
import { NutritionColorIcon } from "shared-ui/global/Icons";
import { useApolloClient } from "@apollo/client";
import { useState } from "react";
import { ConfirmationDialog } from "../../componentLibrary";
import { TWTableActions } from "../../componentLibrary/TWTableActions";
import { TWTabGroup } from "../../componentLibrary/TWTabGroup/TWTabGroup";
import {
  CreateFoodGroupInput,
  EditFoodGroupInput,
  useBulkDeleteSharedFoodGroupsMutation,
  useCreateOrgFoodGroupMutation,
  useCreateSharedFoodGroupMutation,
  useEditOrgFoodGroupMutation,
  useEditSharedFoodGroupMutation,
  useOrgFoodGroupsTableQuery,
  useSharedFoodGroupsTableQuery,
} from "../../types";
import { useOffsetPagination } from "../../utils/pagination";
import { useSnackbar } from "../Snackbar/SnackbarContext";
import { FoodGroupModalEdit } from "./Modal/Edit";
import { FoodGroupModalNew } from "./Modal/New";
import { FoodGroupTable, IFoodGroup } from "./Table";
import { shouldSkipOrgFoodGroupsQuery } from "./dashboardQueryUtils";
import { makeUpdateCacheCreateFoodGroup, makeUpdateCacheEditFoodGroup } from "./utils";

export interface FoodGroupDashboardProps {
  isStaff?: boolean;
}

export const FoodGroupDashboard = ({ isStaff }: FoodGroupDashboardProps) => {
  const CUSTOM = "Custom Food Groups";
  const STANDARD = "Food Groups";
  const standardTab = {
    label: STANDARD,
    icon: <NutritionColorIcon />,
  };

  const [selected, setSelected] = useState(isStaff ? STANDARD : CUSTOM);
  const tabs = [CUSTOM, standardTab];
  const { setMessage } = useSnackbar();
  const apolloClient = useApolloClient();
  const [selectedFoodGroup, setSelectedFoodGroup] = useState<IFoodGroup | null>(null);
  const [foodGroupModalOpen, setFoodGroupModalOpen] = useState(false);
  const [sortOrder, setSortOrder] = useState<"ASC" | "DESC">("ASC");
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [confirmBulkDeleteOpen, setConfirmBulkDeleteOpen] = useState(false);
  const paginationHooks = useOffsetPagination();
  const { queryText = "", onChangeQueryText, limit, offset, query } = paginationHooks;
  const orgQueryResult = useOrgFoodGroupsTableQuery({
    skip: shouldSkipOrgFoodGroupsQuery(isStaff),
    variables: {
      query,
      input: {
        limit,
        offset,
      },
      sortOrder,
    },
  });
  const sharedQueryResult = useSharedFoodGroupsTableQuery({
    variables: { query, input: { limit, offset }, sortOrder },
  });
  const updateCacheCreateFoodGroup = makeUpdateCacheCreateFoodGroup({ scope: isStaff ? "shared" : "org" });
  const updateCacheEditFoodGroup = makeUpdateCacheEditFoodGroup({ scope: isStaff ? "shared" : "org" });
  const [createOrgFoodGroup] = useCreateOrgFoodGroupMutation({});
  const [editOrgFoodGroup] = useEditOrgFoodGroupMutation({});
  const [createSharedFoodGroup] = useCreateSharedFoodGroupMutation();
  const [editSharedFoodGroup] = useEditSharedFoodGroupMutation({});
  const [bulkDeleteSharedFoodGroups, { loading: bulkDeleteLoading }] = useBulkDeleteSharedFoodGroupsMutation({
    onCompleted: data => {
      const deletedFoodGroupIds = data.bulkDeleteSharedFoodGroups.foodGroupIds;
      deletedFoodGroupIds.forEach(id => {
        apolloClient.cache.evict({ id: `FoodGroup:${id}` });
      });
      apolloClient.cache.evict({ fieldName: "sharedFoodGroupsOffsetConnection" });
      apolloClient.cache.evict({ fieldName: "sharedFoodGroupsCursorConnection" });
      apolloClient.cache.evict({ fieldName: "sharedFoodGroups" });
      apolloClient.cache.gc();
      setSelectedIds([]);
      setConfirmBulkDeleteOpen(false);
      const count = deletedFoodGroupIds.length;
      setMessage("success", `${count} food group${count === 1 ? "" : "s"} deleted`);
    },
    onError: e => setMessage("error", e.message),
  });
  const createFoodGroup = isStaff ? createSharedFoodGroup : createOrgFoodGroup;
  const editFoodGroup = isStaff ? editSharedFoodGroup : editOrgFoodGroup;

  const loading =
    selected === CUSTOM ? !orgQueryResult.data || !!orgQueryResult.loading : !sharedQueryResult.data || !!sharedQueryResult.loading;
  const foodGroups =
    selected === CUSTOM
      ? orgQueryResult.data?.orgFoodGroupsOffsetConnection.edges || []
      : sharedQueryResult.data?.sharedFoodGroupsOffsetConnection.edges || [];
  const total =
    selected === CUSTOM
      ? orgQueryResult.data?.orgFoodGroupsOffsetConnection.pageInfo.total || 0
      : sharedQueryResult.data?.sharedFoodGroupsOffsetConnection.pageInfo.total || 0;

  const handleSetSelected = (selected: string) => {
    onChangeQueryText("");
    setSelected(selected);
    setSelectedIds([]);
  };

  const toggleRow = (rowId: string) =>
    setSelectedIds(prev => (prev.includes(rowId) ? prev.filter(id => id !== rowId) : [...prev, rowId]));

  const toggleAll = (pageRowIds: string[], checked: boolean) =>
    setSelectedIds(prev => {
      if (checked) {
        return Array.from(new Set([...prev, ...pageRowIds]));
      }
      const pageSet = new Set(pageRowIds);
      return prev.filter(id => !pageSet.has(id));
    });

  const onCreateFoodGroup = async ({ name, source, foodIds }: CreateFoodGroupInput) => {
    const input = {
      name,
      source,
      foodIds,
    };
    createFoodGroup({
      variables: { input },
      update: (cache, mutationResult) => {
        updateCacheCreateFoodGroup({ cache, mutationResult });
        setMessage("success", `Success! Created Food Group '${name}.'`);
        setSelectedFoodGroup(null);
        setFoodGroupModalOpen(false);
      },
    });
  };

  const onEditFoodGroup = async ({ id, foodGroup }: EditFoodGroupInput) => {
    const input = {
      id,
      foodGroup,
    };
    editFoodGroup({
      variables: { input },
      update: (cache, mutationResult) => {
        updateCacheEditFoodGroup({ cache, mutationResult });
        setMessage("success", `Success! Updated Food Group '${foodGroup.name}.'`);
        setSelectedFoodGroup(null);
        setFoodGroupModalOpen(false);
      },
    });
  };

  const handleBulkDelete = () => {
    if (bulkDeleteLoading || selectedIds.length === 0) {
      return;
    }
    bulkDeleteSharedFoodGroups({
      variables: { input: { foodGroupIds: selectedIds } },
    });
  };

  return (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column", gap: 2, position: "relative" }}>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end" }}>
        <Box>{!isStaff && <TWTabGroup tabs={tabs} onSelected={handleSetSelected} />}</Box>
        <Box sx={{ display: "flex", gap: 2, justifyContent: "flex-end", alignItems: "flex-end" }}>
          <TextField
            label="Search"
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
            }}
            value={queryText}
            onChange={e => onChangeQueryText(e.target.value)}
          />
          <Button disabled={selected === STANDARD && !isStaff} onClick={() => setFoodGroupModalOpen(true)}>
            Add Food Group
          </Button>
        </Box>
      </Box>
      {loading ? (
        <Loading />
      ) : (
        <>
          <FoodGroupTable
            readOnly={selected === CUSTOM || isStaff}
            selectable={Boolean(isStaff)}
            selectedIds={selectedIds}
            onToggleRowSelection={toggleRow}
            onToggleAllSelection={toggleAll}
            setFoodGroupModalOpen={setFoodGroupModalOpen}
            setSelectedFoodGroup={setSelectedFoodGroup}
            rows={[...foodGroups]}
            paginationHooks={paginationHooks}
            total={total}
            setSortOrder={setSortOrder}
            sortOrder={sortOrder}
          />
          {foodGroupModalOpen &&
            (selectedFoodGroup ? (
              <FoodGroupModalEdit
                readOnly={selected === STANDARD && !isStaff}
                open={!!foodGroupModalOpen}
                onClose={() => {
                  setSelectedFoodGroup(null);
                  setFoodGroupModalOpen(false);
                }}
                onEditFoodGroup={onEditFoodGroup}
                foodGroupId={selectedFoodGroup.id}
              />
            ) : (
              <FoodGroupModalNew
                open={!!foodGroupModalOpen}
                onClose={() => {
                  setSelectedFoodGroup(null);
                  setFoodGroupModalOpen(false);
                }}
                onCreateFoodGroup={onCreateFoodGroup}
              />
            ))}
        </>
      )}
      {isStaff && selectedIds.length > 0 && (
        <TWTableActions
          selectedCount={selectedIds.length}
          onClearSelection={() => setSelectedIds([])}
          actions={[{ id: "delete", label: "Delete", onClick: () => setConfirmBulkDeleteOpen(true) }]}
        />
      )}
      {confirmBulkDeleteOpen && (
        <ConfirmationDialog
          open={confirmBulkDeleteOpen}
          title="Delete Food Groups"
          message={`Are you sure that you would like to delete ${selectedIds.length} food group${
            selectedIds.length === 1 ? "" : "s"
          }?`}
          onCancel={() => {
            if (!bulkDeleteLoading) {
              setConfirmBulkDeleteOpen(false);
            }
          }}
          onConfirm={handleBulkDelete}
          variant="containedDestructive"
          loading={bulkDeleteLoading}
        />
      )}
    </Box>
  );
};
